import React from 'react';
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Trash2, Edit2, X, Check, GripVertical, ChevronUp, ChevronDown } from "lucide-react";
import { formatDistanceToNow } from 'date-fns';
import { hu } from 'date-fns/locale';

interface GameInfo {
    id: number;
    content: string;
    position: number;
    user: {
        id: number;
        name: string;
    };
    created_at: string;
    updated_at: string;
}

interface GameInfoItemProps {
    info: GameInfo;
    isOwner: boolean;
    isAdmin: boolean;
    isEditing: boolean;
    editingContent: string;
    index: number;
    totalItems: number;
    onEdit: () => void;
    onUpdate: () => void;
    onDelete: () => void;
    onCancel: () => void;
    onEditingContentChange: (content: string) => void;
    onMoveUp?: () => void;
    onMoveDown?: () => void;
}

const GameInfoItem: React.FC<GameInfoItemProps> = ({
    info,
    isOwner,
    isAdmin,
    isEditing,
    editingContent,
    index,
    totalItems,
    onEdit,
    onUpdate,
    onDelete,
    onCancel,
    onEditingContentChange,
    onMoveUp,
    onMoveDown
}) => {
    const formatDate = (dateString: string) => {
        try {
            return formatDistanceToNow(new Date(dateString), { addSuffix: true, locale: hu });
        } catch (error) {
            return dateString;
        }
    };

    const isModified = info.updated_at !== info.created_at;

    return (
        <div className={`game-info-item mb-3 p-4 rounded ${isAdmin ? 'bg-danger text-white border-2 border-danger' : 'border-left'}`} style={isAdmin ? {} : { borderLeftWidth: '4px', borderLeftColor: '#ffc107', backgroundColor: '#e76621', color: '#ffffff' }}>
            <div className="d-flex justify-content-between align-items-start">
                <div className="flex-grow-1">
                    {isEditing ? (
                        <div className="game-info-editing">
                            <Textarea
                                value={editingContent}
                                onChange={(e) => onEditingContentChange(e.target.value)}
                                className="form-control bg-secondary text-light mb-2"
                                rows={4}
                                maxLength={2000}
                            />
                            <small className="text-muted d-block mb-2">
                                {editingContent.length}/2000 karakter
                            </small>
                            <div className="d-flex gap-2">
                                <Button
                                    onClick={onUpdate}
                                    size="sm"
                                    className="btn btn-success"
                                >
                                    <Check size={16} className="me-1" />
                                    Mentés
                                </Button>
                                <Button
                                    onClick={onCancel}
                                    size="sm"
                                    className="btn btn-secondary"
                                >
                                    <X size={16} className="me-1" />
                                    Mégse
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="game-info-header mb-2">
                                {isAdmin && (
                                    <span className="badge bg-danger text-white me-2">Admin infó</span>
                                )}
                                {!isAdmin && (
                                    <span className="badge ">
                                        {info.user.name}
                                    </span>
                                )}
                                <small className="text-muted ms-2">
                                    {formatDate(info.created_at)}
                                    {isModified && (
                                        <>
                                            {' - '}
                                            <span className="text-info">módosítva: {formatDate(info.updated_at)}</span>
                                        </>
                                    )}
                                </small>
                            </div>

                            <div className="game-info-content mt-2 text-light">
                                <p className="mb-0" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                    {info.content}
                                </p>
                            </div>
                        </>
                    )}
                </div>

                {(isOwner || isAdmin) && !isEditing && (
                    <div className="game-info-actions ms-2 d-flex gap-2 align-items-center">
                        {isAdmin && (
                            <div className="d-flex gap-1">
                                <Button
                                    onClick={onMoveUp}
                                    disabled={index === 0}
                                    size="sm"
                                    variant="outline"
                                    className="btn btn-light btn-sm"
                                    title="Felfelé"
                                >
                                    <ChevronUp size={16} />
                                </Button>
                                <Button
                                    onClick={onMoveDown}
                                    disabled={index === totalItems - 1}
                                    size="sm"
                                    variant="outline"
                                    className="btn btn-light btn-sm"
                                    title="Lefelé"
                                >
                                    <ChevronDown size={16} />
                                </Button>
                            </div>
                        )}
                        <Button
                            onClick={onEdit}
                            size="sm"
                            variant="outline"
                            className={isAdmin ? "btn btn-light btn-sm" : "btn btn-warning btn-sm"}
                            title="Szerkesztés"
                        >
                            <Edit2 size={16} />
                        </Button>
                        <Button
                            onClick={onDelete}
                            size="sm"
                            variant="destructive"
                            className={isAdmin ? "btn btn-light btn-sm" : "btn btn-warning btn-sm"}
                            title="Törlés"
                        >
                            <Trash2 size={16} />
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default GameInfoItem;
